/**
 * Recovery from a failed chunk preload (HS-1264, acceptance criterion 4).
 *
 * A code-split route is fetched when the user navigates to it, long after `index.html` was
 * loaded. If the referenced chunk cannot be fetched, Vite's loader rejects and the route dies on
 * `Unable to preload CSS for …` / `Failed to fetch dynamically imported module …`. The error
 * boundary catches it and shows "This screen could not be loaded", which is a dead end: the user
 * is holding an `index.html` that names files the server will not give it, and no amount of
 * clicking inside that document can fix it.
 *
 * The usual cause is a deploy that happened while the tab was open — the new build removed the
 * hashed chunk the old document still points at. A single full reload fetches the new
 * `index.html` and the problem is gone, so that is what this does.
 *
 * It does NOT paper over HS-1264 itself: there the file was genuinely absent from the image, and
 * reloading would have failed just as hard. That is why the reload is attempted once per chunk
 * and then gives up: a chunk that is missing because the build is broken surfaces as the error
 * screen rather than an infinite reload loop. `scripts/check-bundle.mjs` is what keeps such a
 * build from shipping; this is the seatbelt for the ordinary case.
 */

/** Survives the reload, and only the reload — a new tab starts with a clean slate. */
const STORAGE_KEY = 'smartsash.admin.preload-reloaded';

/** `sessionStorage` throws in some privacy modes; a recovery aid must never be the thing that breaks. */
function readAttempted(): string[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string') : [];
  } catch {
    return [];
  }
}

function rememberAttempt(chunk: string, attempted: string[]): boolean {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...attempted, chunk]));
    return true;
  } catch {
    // Without a memory of the attempt a reload would loop, so decline to reload at all.
    return false;
  }
}

/**
 * The chunk the failure is about. Vite's payload is a plain `Error` whose message embeds the URL;
 * there is no structured field for it. Falling back to the message keeps distinct failures
 * distinct even if the wording changes — the point is only that two different chunks are two
 * different keys.
 */
function failedChunk(payload: unknown): string {
  const message = payload instanceof Error ? payload.message : String(payload);
  return /https?:\/\/\S+/.exec(message)?.[0] ?? message;
}

/** One listener per document; `main.tsx` calls this once, and a second call is a no-op. */
let registered = false;

export function bootstrapChunkReload(): void {
  if (registered) return;
  registered = true;

  window.addEventListener('vite:preloadError', (event) => {
    const chunk = failedChunk((event as Event & { payload?: unknown }).payload);
    const attempted = readAttempted();
    // Already tried for this chunk: the file is missing for real. Let the error boundary have it.
    if (attempted.includes(chunk)) return;
    if (!rememberAttempt(chunk, attempted)) return;

    // Stops Vite from rethrowing while the reload is in flight.
    event.preventDefault();
    window.location.reload();
  });
}
